import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Play, Eye, Download, Loader2, ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { getGames, getDraws, createDraw, runDraw, revealDraw, actaPdfUrl } from "@/lib/api";
import { TIPO_LABEL, isVisibleTipo, formatResultado, formatDate, truncateHash } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Draws() {
  const { user } = useAuth();
  const canWrite = user?.rol === "admin" || user?.rol === "operador";
  const [games, setGames] = useState([]);
  const [draws, setDraws] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [form, setForm] = useState({ game_id: "", nombre: "", client_seed: "", nonce: "1" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => {
    getDraws({ limit: 200 }).then(setDraws).catch(() => {});
  };
  useEffect(() => {
    getGames({ activo: true }).then((g) => { const vis = g.filter((x) => isVisibleTipo(x.tipo)); setGames(vis); if (vis[0]) set("game_id", vis[0].id); }).catch(() => {});
    load();
  }, []);

  const onCreate = async () => {
    if (!form.game_id) return;
    try {
      const payload = { game_id: form.game_id, nombre: form.nombre || undefined, client_seed: form.client_seed || undefined, nonce: form.nonce ? parseInt(form.nonce, 10) : undefined };
      await createDraw(payload);
      toast.success("Sorteo creado y compromiso publicado");
      setOpen(false);
      setForm((f) => ({ ...f, nombre: "", client_seed: "" }));
      load();
    } catch {
      toast.error("No se pudo crear el sorteo");
    }
  };

  const doAction = async (id, action) => {
    setBusy(id + action);
    try {
      if (action === "run") { await runDraw(id); toast.success("Sorteo ejecutado"); }
      if (action === "reveal") { await revealDraw(id); toast.success("Semilla revelada"); }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en la operación");
    } finally { setBusy(null); }
  };

  const visibleDraws = draws.filter((d) => isVisibleTipo(d.tipo));

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-2xl font-bold">Sorteos</h1><p className="text-sm text-muted-foreground">Flujo: comprometer → ejecutar → revelar.</p></div>
        {canWrite && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="gap-2" data-testid="draws-new-button"><Plus className="h-4 w-4" /> Nuevo sorteo</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Crear sorteo (publica el compromiso)</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Juego</Label>
                <Select value={form.game_id} onValueChange={(v) => set("game_id", v)}><SelectTrigger data-testid="draws-form-game"><SelectValue placeholder="Selecciona un juego" /></SelectTrigger>
                  <SelectContent>{games.map((g) => <SelectItem key={g.id} value={g.id}>{g.nombre}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Nombre (opcional)</Label><Input value={form.nombre} onChange={(e) => set("nombre", e.target.value)} placeholder="Sorteo del día…" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Semilla cliente (opcional)</Label><Input value={form.client_seed} onChange={(e) => set("client_seed", e.target.value)} placeholder="auto" data-testid="draws-form-client-seed" /></div>
                <div><Label>Nonce</Label><Input value={form.nonce} onChange={(e) => set("nonce", e.target.value)} data-testid="draws-form-nonce" /></div>
              </div>
              <p className="text-xs text-muted-foreground">Al crear, se genera la semilla del servidor y se publica su hash (compromiso) antes del sorteo.</p>
            </div>
            <DialogFooter><Button onClick={onCreate} data-testid="draws-form-submit">Crear y comprometer</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        )}
      </div>

      <Card className="mt-6">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Sorteo</TableHead><TableHead>Tipo</TableHead><TableHead>Estado</TableHead>
                <TableHead>Resultado</TableHead><TableHead>Compromiso</TableHead><TableHead>Fecha</TableHead><TableHead className="text-right">Acciones</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {visibleDraws.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">No hay sorteos. Crea el primero.</TableCell></TableRow>
                ) : visibleDraws.map((d) => (
                  <TableRow key={d.id} data-testid={`draws-row-${d.id}`}>
                    <TableCell className="max-w-[180px] truncate font-medium">{d.nombre}</TableCell>
                    <TableCell>{TIPO_LABEL[d.tipo]}</TableCell>
                    <TableCell><StatusBadge estado={d.estado} /></TableCell>
                    <TableCell className="font-medium">{formatResultado(d.tipo, d.resultado)}</TableCell>
                    <TableCell className="font-mono text-xs">{truncateHash(d.commitment, 6)}</TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{formatDate(d.created_at)}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        {canWrite && d.estado === "comprometido" && (
                          <Button size="sm" className="gap-1" disabled={busy === d.id + "run"} onClick={() => doAction(d.id, "run")} data-testid={`draws-run-${d.id}`}>
                            {busy === d.id + "run" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} Ejecutar
                          </Button>
                        )}
                        {canWrite && d.estado === "ejecutado" && (
                          <Button size="sm" variant="secondary" className="gap-1" disabled={busy === d.id + "reveal"} onClick={() => doAction(d.id, "reveal")} data-testid={`draws-reveal-${d.id}`}>
                            {busy === d.id + "reveal" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />} Revelar
                          </Button>
                        )}
                        <Link to={`/sorteo/${d.id}`} target="_blank"><Button size="sm" variant="ghost" className="gap-1"><ExternalLink className="h-3.5 w-3.5" /></Button></Link>
                        <a href={actaPdfUrl(d.id)} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost"><Download className="h-3.5 w-3.5" /></Button></a>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
